import { substanceArray, substanceMap, substanceThresholds } from './substancesAndColors';
import { indexOfMax } from '../utils/helpers';

export const benzodiazepineLabels = ['Alprazolam', 'Bromazepam', 'Bromazolam', 'Clonazepam', 'Desalkylgidazepam', 'Diazepam', 'Etizolam', 'Flubromazepam', 'Flurazepam', 'Lorazepam', 'Oxazepam', 'Temazepam', 'Triazolam'];
export const xylazineLabels = ['Xylazine', 'Xylazine hydrochloride'];
export const fentanylLabels = ['Acetylfentanyl', 'Carfentanil', 'Despropionyl fentanyl', 'Fentanyl', 'Furanylfentanyl', 'Parafluorofentanyl', 'Remifentanil'];
export const medetomidineLabels = ['Medetomidine', 'Dexmedetomidine', 'Dexmedetomidine hydrochloride'];

const lowPowerMw = 45;
const heroinResidualThreshold = 0.0125;
const heroinSignalToNoiseThreshold = 20;
const reconstructionErrorThreshold = 0.000145;
const signalThreshold = 750;
const dexmedetomidineHclML1TraceThreshold = 0.045;
const approvedMedetomidineLabels = ['Caffeine', 'Dimethyl sulfone', 'Erythritol', 'Mannitol'];

export const classifySpectrum = (processedSpectrum, predictionArray, reconstructionError, rawSpectrum, benzodiazepinePrediction) => {
    let classification = 'Unknown'
    // let maxRawValue = Math.max(...rawSpectrum);
    // if (maxRawValue >= 65535) { return 'Unknown' } // Causes unknowns because additive raw spectrum is too high
    let maxProcessedValue = Math.max(...processedSpectrum);
    if (maxProcessedValue < signalThreshold) {
        classification = 'No Signal';
    }
    else if (processedSpectrum.indexOf(maxProcessedValue) > 1576) {
        classification = 'Unknown';
    }
    else if (reconstructionError > reconstructionErrorThreshold) {
        classification = 'Unknown';
        let index = indexOfMax(predictionArray[0]);
        if (
            ['Caffeine', 'Dimethyl sulfone', 'Mannitol', 'Erythritol', 'Inositol', 'Sorbitol', 'Xylitol'].includes(substanceArray[index])
            && predictionArray[0][index] > 0.42
            && reconstructionError < 0.00024
        ) {
            classification = substanceArray[index];
        }
    }
    else {
        let index = indexOfMax(predictionArray[0]);
        classification = predictionArray[0][index] > substanceThresholds[substanceArray[index]] ? substanceArray[index] : 'Unknown';
    }

    if (benzodiazepineLabels.includes(classification)) {
        const topTraceBenzodiazepineResult = benzodiazepineLabels.map((label, i) => ({ label: label, prediction: benzodiazepinePrediction[i] })).sort((a, b) => b.prediction - a.prediction)[0]
        if (topTraceBenzodiazepineResult.prediction > 0.2) {
            classification = benzodiazepineLabels.map((label, i) => ({ label: label, prediction: benzodiazepinePrediction[i] })).sort((a, b) => b.prediction - a.prediction)[0].label;
        } else {
            classification = 'Unknown';
        }
    }

    return classification
}

export const detectTraceFentanyl = (label, processedSpectrum, result, fentanylMLOutput, mlOutput) => {
    const approvedLabels = ['Caffeine', 'Dimethyl sulfone', 'Mannitol', 'Erythritol', 'Inositol', 'Sorbitol', 'Xylitol'];
    if (!approvedLabels.includes(label)) { return { hasTraceFentanyl: false, traceFentanylLabel: null } };
    const { maxFEMLValue, maxFEMLIndex } = fentanylMLOutput.reduce((acc, value, index) => {
        if (value > acc.maxFEMLValue) {
            return { maxFEMLValue: value, maxFEMLIndex: index };
        }
        return acc;
    }, { maxFEMLValue: -Infinity, maxFEMLIndex: -1 });
    const fentanylLabel = fentanylLabels[maxFEMLIndex];
    const rankings = mlOutput.map(e => e[0]);
    const indexTopBase = rankings.findIndex(name => name.toLowerCase().includes('fent'));
    const maxMLValue = mlOutput[indexTopBase][1];
    const maxInRegion = Math.max(...processedSpectrum.slice(1000 - 225 - 5, 1000 - 225 + 5 + 1));
    const isPeak = Math.max(...processedSpectrum.slice(1000 - 225 - 10, 1000 - 225 - 5)) < maxInRegion && Math.max(...processedSpectrum.slice(1000 - 225 + 6, 1000 - 225 + 11)) < maxInRegion;
    let threshold = 0.75;
    threshold = maxMLValue > 0.01 ? 0.035 : threshold;
    threshold = maxMLValue > 0.05 ? 0.02 : threshold;
    threshold = (isPeak && maxMLValue > 0.01) ? 0.015 : threshold;
    if (maxFEMLValue >= threshold && processedSpectrum[1000 - 225] > 30) {
        return { hasTraceFentanyl: true, traceFentanylLabel: 'Fentanyl' };
    }
    return { hasTraceFentanyl: false, traceFentanylLabel: null };
}

export const detectTraceHydromorphone = (label, processedSpectrum, result, hydromorphonePrediction) => {
    if (!['Lactose', 'alpha-Lactose'].includes(label)) { return false; }
    if (hydromorphonePrediction[1] > 0.55 && result[0][substanceMap.get('Hydromorphone')] >= 0.01) { return true; } else { return false; }
}

export const detectTraceHeroin = (point) => {
    if (point?.label !== 'Acetaminophen' || Math.round(Number(point?.power)) !== lowPowerMw) { return false; }

    const heroinML2 = Number(point?.result?.[2]?.[substanceMap.get('Heroin')]);
    const autocorrelationWidth = Number(point?.autocorrelationWidth);
    const lowFrequencyEnergy = Number(point?.lowFrequencyEnergy);
    const signalToNoise = Number(point?.signalToNoise);
    const reconstructionError = Number(point?.reconstructionError);

    if (![heroinML2, autocorrelationWidth, lowFrequencyEnergy, signalToNoise, reconstructionError].every(Number.isFinite)) {
        return false;
    }
    if (signalToNoise <= heroinSignalToNoiseThreshold) { return false; }

    const expectedML2 =
        0.003772
        + (-6.69e-05 * autocorrelationWidth)
        + (1.43e-04 * lowFrequencyEnergy)
        + (-3.16e-04 * signalToNoise)
        + (21.69 * reconstructionError);

    return heroinML2 - expectedML2 > heroinResidualThreshold;
}

export const shouldShowTraceHeroin = (points) => points.filter(point => point.traceDetections?.Heroin).length >= 2;

export const detectTraceMedetomidine = (label, result) => {
    if (!approvedMedetomidineLabels.includes(label)) { return false; }

    const dexmedetomidineHclML1 = Number(result?.[1]?.[substanceMap.get('Dexmedetomidine hydrochloride')]);
    return Number.isFinite(dexmedetomidineHclML1) && dexmedetomidineHclML1 >= dexmedetomidineHclML1TraceThreshold;
}

export const detectTraceBenzodiazepine = (label, processedSpectrum, result, benzodiazepinePrediction, mlOutput) => {
    const approvedLabels = ['Caffeine', 'Dimethyl sulfone', 'Mannitol', 'Erythritol', 'Inositol', 'Sorbitol', 'Xylitol', 'Acetylfentanyl', 'Carfentanil', 'Despropionyl fentanyl', 'Fentanyl', 'Furanylfentanyl', 'Parafluorofentanyl', 'Remifentanil', 'Lactose', 'alpha-Lactose'];
    if (!approvedLabels.includes(label)) { return { hasTraceBenzodiazepine: false, traceBenzodiazepineLabel: null } };
    const benzodiazepinePredictionClassification = benzodiazepinePrediction;
    const passingBenzodiazepines = [];
    benzodiazepinePredictionClassification.forEach((e, i) => {

        let baseModelThreshold = 0.01;
        if (benzodiazepineLabels[i] === 'Temazepam') { baseModelThreshold = 0.013; }
        if (benzodiazepineLabels[i] === 'Oxazepam') { baseModelThreshold = 0.08; }

        let traceThreshold = benzodiazepineLabels[i] === ('Bromazolam' || 'Diazepam') ? 0.3 : 0.7;
        traceThreshold = benzodiazepineLabels[i] === 'Oxazepam' ? 0.9 : traceThreshold;

        if (e >= traceThreshold && result[0][substanceMap.get(benzodiazepineLabels[i])] >= baseModelThreshold) {
            const indexML = mlOutput.findIndex(row => row[0] === benzodiazepineLabels[i]);
            const values = [mlOutput[indexML][2], mlOutput[indexML][3]];
            const max = Math.max(...values);
            const min = Math.min(...values);
            const difference = max / min;
            const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
            const variance = values.reduce((sum, value) => sum + Math.pow(value - mean, 2), 0) / values.length;
            const standardDeviation = Math.sqrt(variance);
            passingBenzodiazepines.push({ label: benzodiazepineLabels[i], traceMLValue: e, standardDeviation, difference });
        }
    });
    if (passingBenzodiazepines.length > 0) {
        passingBenzodiazepines.sort((a, b) => a.traceMLValue - b.traceMLValue);
        return { hasTraceBenzodiazepine: true, traceBenzodiazepineLabel: passingBenzodiazepines[0].label }
    }
    return { hasTraceBenzodiazepine: false, traceBenzodiazepineLabel: null };

}

export const detectTraceXylazine = (label, processedSpectrum, result, mlOutput, xylazinePrediction) => {
    const approvedLabels = ['Caffeine', 'Dimethyl sulfone', 'Mannitol', 'Erythritol', 'Inositol', 'Sorbitol', 'Xylitol', 'Acetylfentanyl', 'Carfentanil', 'Despropionyl fentanyl', 'Fentanyl', 'Furanylfentanyl', 'Parafluorofentanyl', 'Remifentanil'];
    if (!approvedLabels.includes(label)) { return { hasTraceXylazine: false, traceXylazineLabel: null } };
    const xylazinePredictionClassification = xylazinePrediction;
    const passingXylazines = [];
    xylazinePredictionClassification.forEach((e, i) => {
        if (e >= 0.15) {
            const indexML = mlOutput.findIndex(row => row[0] === xylazineLabels[i]);
            const values = [mlOutput[indexML][2], mlOutput[indexML][3]];
            const max = Math.max(...values);
            const min = Math.min(...values);
            const difference = max / min;
            const mean = values.reduce((sum, value) => sum + value, 0) / values.length;
            const variance = values.reduce((sum, value) => sum + Math.pow(value - mean, 2), 0) / values.length;
            const standardDeviation = Math.sqrt(variance);
            let threshold = e >= 0.9 ? 0.015 : 0.02;
            if (min >= threshold) {
                passingXylazines.push({ label: xylazineLabels[i], traceMLValue: e, standardDeviation, difference });
            }
        }
    });
    if (passingXylazines.length > 0) {
        return { hasTraceXylazine: true, traceXylazineLabel: 'Xylazine' }
    }
    return { hasTraceXylazine: false, traceXylazineLabel: null };
}
